import { useState, useEffect } from 'react';
import logo from '../assets/logo.png';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const isAppInstalled = localStorage.getItem('appInstalled') === 'true' || 
                         (window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone);

    if (isAppInstalled) {
      localStorage.setItem('appInstalled', 'true');
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      const lastDismissed = localStorage.getItem('installBannerDismissed');
      const thirtyMinutes = 30 * 60 * 1000;
      
      if (!lastDismissed || (Date.now() - parseInt(lastDismissed, 10)) > thirtyMinutes) {
        setShowInstallBanner(true);
      }
    };

    // Add event listener for custom install button click
    const handleInstallButtonClick = () => {
      if (deferredPrompt) {
        handleInstallClick();
      } else {
        // If deferredPrompt isn't available, show the banner
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('showInstallPrompt', handleInstallButtonClick);

    window.addEventListener('appinstalled', () => {
      console.log('App was installed');
      localStorage.setItem('appInstalled', 'true');
      setShowInstallBanner(false);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('showInstallPrompt', handleInstallButtonClick);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User response to the install prompt: ${outcome}`);
    
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('installBannerDismissed', Date.now().toString());
  };

  if (!showInstallBanner) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 shadow-xl rounded-xl p-4 max-w-sm z-50 border border-gray-200 dark:border-gray-700 transform transition-all duration-300 hover:scale-105">
      <div className="flex items-start">
        <div className="flex-shrink-0 mr-3">
          <div className="relative">
            <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-emerald-100 dark:border-emerald-900 p-1 bg-white dark:bg-gray-800">
              <img 
                src={logo} 
                alt="Tinat Academy Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white rounded-full p-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              Install Tinat Academy
            </h3>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Get the full experience. Add to your home screen for quick access.
          </p>
          <div className="mt-3 flex space-x-2">
            <button
              type="button"
              onClick={handleInstallClick}
              className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
            >
              <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Install
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="flex-1 inline-flex justify-center items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
            >
              Not Now
            </button>
          </div>
        </div>
      </div>
      <div className="mt-2 text-xxs text-gray-400 dark:text-gray-500 text-center">
        Works offline â€¢ No app store required
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
